import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../../../../utils/api";
import {
  Card,
  Button,
  PageHeader,
  AdminLayout,
  Grid,
  Badge,
  LoadingSpinner,
  EmptyState,
  useToast,
  Modal,
  PaginationControls,
} from "../components/AdminSharedComponents";
import {
  AlertTriangle,
  Calendar,
  Package,
  Star,
  Plus,
  Search,
  X,
  Trash2,
} from "lucide-react";

import { FilterContainer } from "../components/ui/FilterContainer";
import { SearchInput } from "../components/ui/SearchInput";
import { DropdownFilter } from "../components/ui/DropdownFilter";

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedSort, setSelectedSort] = useState("name_asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  const toast = useToast();
  const productsPerPage = 10;

  // Fetch products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/api/products");
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      setError("Failed to load products. Please try again later.");
      toast.error("Gagal memuat produk");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Delete
  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/products/${id}`);
      setProducts(products.filter((product) => product.id !== id));
      setIsDeleteModalOpen(false);
      toast.success("Produk berhasil dihapus");
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Gagal menghapus produk");
    }
  };

  // Toggle featured
  const toggleFeatured = async (id, currentStatus) => {
    try {
      await api.patch(`/api/products/${id}`, {
        is_featured: !currentStatus,
      });
      setProducts(
        products.map((product) =>
          product.id === id
            ? { ...product, is_featured: !currentStatus }
            : product
        )
      );
      toast.success(`Product ${!currentStatus ? "featured" : "unfeatured"}`);
    } catch (error) {
      console.error("Error updating featured status:", error);
      toast.error("Failed to update product status");
    }
  };

  // Data
  const featuredProducts = products.filter((product) => product.is_featured);
  const nonFeaturedProducts = products.filter(
    (product) => !product.is_featured
  );

  const categories = [
    "all",
    ...new Set(products.map((p) => p.category).filter(Boolean)),
  ];

  const filteredNonFeaturedProducts = nonFeaturedProducts.filter(
    (product) =>
      (selectedCategory === "all" || product.category === selectedCategory) &&
      (product.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const sortOptions = [
    { label: "Name A-Z", value: "name_asc" },
    { label: "Name Z-A", value: "name_desc" },
    { label: "Date Newest", value: "date_desc" },
    { label: "Date Oldest", value: "date_asc" },
  ];

  const [sortField, sortDir] = selectedSort.split("_");
  const sortedProducts = [...filteredNonFeaturedProducts].sort((a, b) => {
    if (sortField === "name") {
      return sortDir === "asc"
        ? a.title.localeCompare(b.title)
        : b.title.localeCompare(a.title);
    } else if (sortField === "date") {
      return sortDir === "asc"
        ? new Date(a.createdAt) - new Date(b.createdAt)
        : new Date(b.createdAt) - new Date(a.createdAt);
    }
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedProducts.length / productsPerPage);
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = sortedProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  // Skeleton
  const ProductCardSkeleton = () => (
    <Card className="animate-pulse">
      <div className="h-40 bg-gray-200 rounded mb-4"></div>
      <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
      <div className="flex justify-between">
        <div className="h-8 bg-gray-200 rounded w-24"></div>
        <div className="h-8 bg-gray-200 rounded w-24"></div>
      </div>
    </Card>
  );

  // Card
  const ProductCard = ({ product, isFeatured = false }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <Card
        key={product.id}
        className={`relative overflow-hidden transition-all duration-200 ${
          isFeatured
            ? "border-2 border-primary bg-primary-50"
            : "hover:shadow-lg"
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isFeatured && (
          <div className="absolute top-0 left-0 w-full bg-primary text-white py-1 px-3 text-center text-xs font-medium">
            Featured Product
          </div>
        )}
        <div
          className={`flex justify-center ${isFeatured ? "mt-6" : "mt-2"} mb-4`}
        >
          {product.image_path ? (
            <img
              src={`${process.env.REACT_APP_API_URL}${product.image_path}`}
              alt={product.title}
              className="h-40 object-contain rounded"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src =
                  "https://via.placeholder.com/150?text=No+Image";
              }}
            />
          ) : (
            <div className="h-40 w-full rounded flex items-center justify-center bg-gray-50">
              <span className="text-gray-400 text-sm">No image available</span>
            </div>
          )}
        </div>
        <h3
          className={`font-semibold text-lg ${
            isFeatured ? "text-primary-dark" : "text-gray-800"
          }`}
        >
          {product.title}
        </h3>
        <p className="text-sm text-gray-600 mt-2 line-clamp-2 h-10">
          {product.description?.length > 80
            ? `${product.description.substring(0, 80)}...`
            : product.description || "No description available"}
        </p>
        {product.createdAt && (
          <div className="flex items-center mt-3 text-xs text-gray-500">
            <Calendar className="h-4 w-4 mr-1" />
            {new Date(product.createdAt).toLocaleDateString()}
          </div>
        )}
        <div
          className={`
            absolute inset-x-0 bottom-0 bg-white bg-opacity-95 border-t border-gray-100
            transition-all duration-300 p-3 space-y-2 z-10
            ${isHovered ? "opacity-100 visible" : "opacity-0 invisible"}
          `}
          style={{
            transform: isHovered ? "translateY(0)" : "translateY(100%)",
            transition:
              "transform 0.3s ease, opacity 0.3s ease, visibility 0.3s",
          }}
        >
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              className="flex-1 py-1.5 text-center text-sm"
              onClick={() =>
                (window.location.href = `/admin/products/detail/${product.id}`)
              }
            >
              View
            </Button>
            <Button
              variant="secondary"
              className="flex-1 py-1.5 text-center text-sm"
              onClick={() =>
                (window.location.href = `/admin/products/edit/${product.id}`)
              }
            >
              Edit
            </Button>
          </div>
          <div className="flex space-x-2">
            <Button
              variant={isFeatured ? "primary" : "outline"}
              className="flex-1 py-1.5 text-center text-sm"
              onClick={() => toggleFeatured(product.id, product.is_featured)}
            >
              {isFeatured ? "Unfeature" : "Feature"}
            </Button>
            <Button
              variant="danger"
              className="flex-1 py-1.5 text-center text-sm"
              onClick={() => handleDeleteClick(product)}
            >
              Delete
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  // Loading state
  if (loading) {
    return (
      <AdminLayout>
        <PageHeader title="Product Management" subtitle="Kelola semua produk" />
        <Card className="mb-6">
          <div className="h-10 bg-gray-200 rounded-lg"></div>
        </Card>
        <Card title="Featured Products">
          <Grid cols={{ default: 1, md: 2, lg: 4 }} gap={4}>
            {[...Array(4)].map((_, index) => (
              <ProductCardSkeleton key={index} />
            ))}
          </Grid>
        </Card>
        <Card title="Regular Products">
          <Grid cols={{ default: 1, md: 2, lg: 3 }} gap={4}>
            {[...Array(6)].map((_, index) => (
              <ProductCardSkeleton key={index} />
            ))}
          </Grid>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <PageHeader
        title="Product Management"
        actions={
          <Button
            variant="primary"
            onClick={() => (window.location.href = "/admin/products/add")}
            className="flex items-center justify-center gap-1"
          >
            <Plus size={16} />
            Add New Product
          </Button>
        }
      />

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex justify-between items-center">
          <span>{error}</span>
          <Button variant="secondary" onClick={fetchProducts}>
            Retry
          </Button>
        </div>
      )}

      {/* Filters */}
      <FilterContainer>
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search products..."
        />
        <DropdownFilter
          label="Category"
          value={selectedCategory}
          onChange={setSelectedCategory}
          options={categories.map((c) => ({ value: c, label: c }))}
        />
        <DropdownFilter
          label="Sort by"
          value={selectedSort}
          onChange={setSelectedSort}
          options={sortOptions}
        />
      </FilterContainer>

      {/* Featured Products */}
      <Card
        title={
          <div className="flex items-center">
            <Star className="h-5 w-5 mr-2 text-primary" />
            Featured Products{" "}
            <span className="text-sm text-gray-500 ml-2">(Max 4)</span>
          </div>
        }
        className="mb-6"
      >
        {featuredProducts.length === 0 ? (
          <EmptyState
            icon={Star}
            message="No featured products yet. Highlight your best products by marking them as featured."
          />
        ) : (
          <>
            <Grid cols={{ default: 1, md: 2, lg: 4 }} gap={4}>
              {featuredProducts.slice(0, 4).map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  isFeatured={true}
                />
              ))}
            </Grid>
            {featuredProducts.length > 4 && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-100 rounded-md">
                <div className="flex items-center text-yellow-700 text-sm">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  You have {featuredProducts.length} featured products. Only the
                  first 4 will be displayed on the website.
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Regular Products */}
      <Card
        title={
          <div className="flex items-center">
            <Package className="h-5 w-5 mr-2 text-primary" />
            Regular Products
          </div>
        }
      >
        {currentProducts.length === 0 ? (
          <EmptyState
            icon={Package}
            message={
              searchTerm || selectedCategory !== "all"
                ? "No products match your criteria"
                : "No regular products available"
            }
            {...(() => {
              if (products.length === 0) {
                // sama sekali belum ada produk
                return {
                  buttonLabel: "Add First Product",
                  buttonLink: "/admin/products/add",
                };
              } else if (
                products.length > 0 &&
                nonFeaturedProducts.length === 0
              ) {
                // sudah ada produk tapi semua featured
                return {
                  buttonLabel: "Add More Product",
                  buttonLink: "/admin/products/add",
                };
              } else if (searchTerm || selectedCategory !== "all") {
                // hasil filter/search kosong
                return {
                  buttonLabel: "Reset Filters",
                  buttonOnClick: () => {
                    setSearchTerm("");
                    setSelectedCategory("all");
                    setSelectedSort("name_asc");
                  },
                };
              }
              return {};
            })()}
          />
        ) : (
          <>
            <Grid cols={{ default: 1, md: 2, lg: 3 }} gap={4}>
              {currentProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </Grid>
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onNext={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && productToDelete && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title="Hapus Produk"
          actions={
            <>
              <Button
                variant="danger"
                onClick={() => handleDelete(productToDelete.id)}
              >
                Hapus
              </Button>
              <Button
                variant="secondary"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Batal
              </Button>
            </>
          }
        >
          <div className="flex items-start">
            <div className="flex-shrink-0 bg-red-100 rounded-full p-2 mr-3">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-gray-700 font-medium">
                Apakah Anda yakin ingin menghapus produk{" "}
                <span className="text-primary-dark font-semibold">
                  "{productToDelete.title}"
                </span>
                ?
              </p>
              <p className="mt-2 text-sm text-gray-600">
                Tindakan ini tidak dapat dibatalkan.
              </p>
            </div>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
};

export default Products;
